// TL mobile drawer.
class TLMobileDrawer extends HTMLElement {
  constructor() {
    super();
    this._initialised = false;
    this._panelHistory = ['root'];
    this._triggerEl = null;
    this._abortCtrl = null;
    this._inerted = [];
    this._handleOpenEvent = this._handleOpenEvent.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
  }

  connectedCallback() {
    if (this._abortCtrl) return;

    this._abortCtrl = new AbortController();
    const opts = { signal: this._abortCtrl.signal };

    document.addEventListener('tl:drawer:open', this._handleOpenEvent, opts);
    document.addEventListener('shopify:section:load', this._handleSectionLoad, opts);
  }

  _init() {
    if (this._initialised || !this._abortCtrl) return;
    this._initialised = true;

    const opts = { signal: this._abortCtrl.signal };
    this.addEventListener('click', this._onClick, opts);
    document.addEventListener('keydown', this._onKeydown, opts);
    this._syncPanelControls();
  }

  disconnectedCallback() {
    this._abortCtrl?.abort();
    this._abortCtrl = null;
    this._initialised = false;
    this._setBackgroundInert(false);
    document.body.classList.remove('tl-drawer-open');
  }

  // inert page behind drawer
  _setBackgroundInert(enable) {
    if (enable) {
      this._inerted = [];
      let node = this;
      while (node.parentElement) {
        const parent = node.parentElement;
        for (const sibling of Array.from(parent.children)) {
          if (sibling === node || sibling.inert) continue;
          const hadAria = sibling.hasAttribute('aria-hidden');
          sibling.inert = true;
          if (!hadAria) sibling.setAttribute('aria-hidden', 'true');
          this._inerted.push({ el: sibling, hadAria });
        }
        if (parent === document.body) break;
        node = parent;
      }
    } else {
      this._inerted.forEach(({ el, hadAria }) => {
        el.inert = false;
        if (!hadAria) el.removeAttribute('aria-hidden');
      });
      this._inerted = [];
    }
  }

  _handleOpenEvent(event) {
    this._init();
    this._triggerEl = event.detail?.trigger ?? null;
    this.open();
  }

  _onClick(event) {
    const target = event.target;
    if (target.closest('[data-drawer-close]') || target.closest('[data-drawer-backdrop]')) {
      this.close();
      return;
    }
    const openPanelBtn = target.closest('[data-drawer-open-panel]');
    if (openPanelBtn) {
      const panelId = openPanelBtn.getAttribute('data-drawer-open-panel');
      this._navigateToPanel(panelId);
      return;
    }
    if (target.closest('[data-drawer-back]')) {
      this._navigateBack();
    }
  }

  _onKeydown(event) {
    if (this.getAttribute('data-state') !== 'open') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') {
      this._trapFocus(event);
    }
  }

  open() {
    if (this.getAttribute('data-state') === 'open') return;
    this.setAttribute('data-state', 'open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tl-drawer-open');
    this._setBackgroundInert(true);
    // focus dialog, no ring
    const drawer = this.querySelector('.drawer');
    requestAnimationFrame(() => drawer?.focus({ preventScroll: true }));
    if (this._triggerEl?.setAttribute) {
      this._triggerEl.setAttribute('aria-expanded', 'true');
    }
  }

  close() {
    if (this.getAttribute('data-state') !== 'open') return;
    this.setAttribute('data-state', 'closed');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tl-drawer-open');
    this._setBackgroundInert(false);
    this._resetPanels();
    if (this._triggerEl) {
      this._triggerEl.setAttribute('aria-expanded', 'false');
      this._triggerEl.focus();
    }
    this._triggerEl = null;
  }

  _handleSectionLoad(event) {
    if (!event.target?.contains?.(this)) return;
    this.disconnectedCallback();
    this.connectedCallback();
  }

  _navigateToPanel(panelId) {
    const target = this.querySelector(`.drawer__panel[data-panel-id="${CSS.escape(panelId)}"]`);
    if (!target) return;
    const currentPanelId = this._panelHistory[this._panelHistory.length - 1];
    const current = this.querySelector(`.drawer__panel[data-panel-id="${CSS.escape(currentPanelId)}"]`);
    if (!current || current === target) return;

    target.hidden = false;
    target.inert = false;
    requestAnimationFrame(() => {
      current.classList.remove('is-active');
      current.classList.add('is-behind');
      current.inert = true;
      target.classList.add('is-active');
      target.scrollTop = 0;
    });

    this._panelHistory.push(panelId);
    this._syncPanelControls();
    const firstFocusable = this._visibleFocusable(target)[0];
    firstFocusable?.focus({ preventScroll: true });
  }

  _navigateBack() {
    if (this._panelHistory.length <= 1) return;
    const currentPanelId = this._panelHistory.pop();
    const previousPanelId = this._panelHistory[this._panelHistory.length - 1];
    const current = this.querySelector(`.drawer__panel[data-panel-id="${CSS.escape(currentPanelId)}"]`);
    const previous = this.querySelector(`.drawer__panel[data-panel-id="${CSS.escape(previousPanelId)}"]`);
    if (!current || !previous) return;

    previous.classList.remove('is-behind');
    previous.classList.add('is-active');
    previous.inert = false;
    current.classList.remove('is-active');
    current.inert = true;
    current.style.transform = '';

    current.addEventListener('transitionend', () => {
      if (!current.classList.contains('is-active')) {
        current.hidden = true;
      }
    }, { once: true });

    this._syncPanelControls();
    const firstFocusable = this._visibleFocusable(previous)[0];
    firstFocusable?.focus({ preventScroll: true });
  }

  _resetPanels() {
    this._panelHistory = ['root'];
    this.querySelectorAll('.drawer__panel').forEach((panel) => {
      const isRoot = panel.getAttribute('data-panel-id') === 'root';
      panel.classList.toggle('is-active', isRoot);
      panel.classList.remove('is-behind');
      panel.hidden = !isRoot;
      panel.inert = !isRoot;
      panel.scrollTop = 0;
    });
    this._syncPanelControls();
  }

  _trapFocus(event) {
    const drawer = this.querySelector('.drawer');
    if (!drawer) return;

    const focusable = this._visibleFocusable(drawer);
    if (!focusable.length) {
      event.preventDefault();
      drawer.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  _visibleFocusable(root) {
    return Array.from(root.querySelectorAll(this._focusableSelector)).filter((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  _syncPanelControls() {
    const currentPanelId = this._panelHistory[this._panelHistory.length - 1] || 'root';
    this.querySelectorAll('[data-drawer-open-panel]').forEach((control) => {
      const panelId = control.getAttribute('data-drawer-open-panel');
      if (!panelId) return;
      control.setAttribute('aria-expanded', String(panelId === currentPanelId));
      const panel = this.querySelector(`.drawer__panel[data-panel-id="${CSS.escape(panelId)}"]`);
      if (panel?.id) {
        control.setAttribute('aria-controls', panel.id);
      }
    });
  }
}

if (!customElements.get('tl-mobile-drawer')) {
  customElements.define('tl-mobile-drawer', TLMobileDrawer);
}
